import { PerspectiveCamera, Quaternion, Vector3 } from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { reaction } from "mobx";
import { store } from "../Store";
import gsap, { Power2 } from 'gsap';
import { Config } from "../Config";

const NEAR_ZERO = 0.001;

export class CameraController {
  constructor(domElement) {
    this.camera = new PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.01, 500);

    this.lastCameraPosition = new Vector3();

    this.orbitControls = new OrbitControls(this.camera, domElement);
    this.orbitControls.enableDamping = true;

    if (!Config.DEBUG) {
      this.orbitControls.rotateSpeed = 0.5;
      this.orbitControls.minPolarAngle = 1.4;
      this.orbitControls.maxPolarAngle = 1.7;
      this.orbitControls.minAzimuthAngle = -0.5;
      this.orbitControls.maxAzimuthAngle = 0.5;
      this.orbitControls.enablePan = false;
      this.orbitControls.enableZoom = false;
    }

    this.viewpoints = {};
    this._savedPosition = null;
    this._savedQuaternion = null;
    this._targetViewOffsetX = 0;

    reaction(() => store.currentHotspotId, async (id, previousId) => {
      if (previousId === null) {
        this.saveViewpoint();
      }

      this.disableControls();

      await this.tweenToHotspot(id);

      if (id !== null) {
        store.setExitButtonVisible(true);
      } else {
        this.enableControls();
      }
    });
  }

  setViewpoints(viewpoints) {
    viewpoints.forEach(viewpoint => {
      const id = viewpoint.name.split("viewpoint_")[1];

      const position = new Vector3();
      const quaternion = new Quaternion();
      const correction = new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), -Math.PI / 2);

      viewpoint.getWorldPosition(position);
      viewpoint.getWorldQuaternion(quaternion);

      // Fix for incorrect rotations on cameras after gltf export from Blender
      quaternion.multiply(correction);

      this.viewpoints[id] = {
        position,
        quaternion
      }
    });
  }

  saveViewpoint() {
    this._savedPosition = this.camera.position.clone();
    this._savedQuaternion = this.camera.quaternion.clone();
  }

  enableControls() {
    if (this.orbitControls) {
      this.orbitControls.enabled = true;
    }
  }

  disableControls() {
    if (this.orbitControls) {
      this.orbitControls.enabled = false;
    }
  }

  setDistance(distance) {
    if (this.orbitControls) {
      this.orbitControls.minDistance = distance;
      this.orbitControls.maxDistance = distance;
    }
  }

  setTarget = (() => {
    const tmp = new Vector3();
    const defaultOffset = new Vector3(0, 0, 1);
    const defaultQuaternion = new Quaternion();

    return (x, y, z, distance) => {
      if (this.orbitControls) {
        const cameraOffset = this.camera.getWorldDirection(tmp).setLength(distance).negate();

        this.orbitControls.target.set(x, y, z);

        // If a hotspot is currently selected, we don't update the camera position directly but the saved camera position instead.
        if (store.currentHotspotId === null) {          
          this.camera.position.set(x, y, z).add(cameraOffset);
          this.camera.lookAt(this.orbitControls.target);
        } else {
          this._savedPosition.set(x, y, z).add(defaultOffset.setLength(distance));
          this._savedQuaternion = defaultQuaternion;
        }
      }
    }
  })();

  setAzimuthalAngles(min, max) {
    if (this.orbitControls) {
      this.orbitControls.minAzimuthAngle = min;
      this.orbitControls.maxAzimuthAngle = max;
    }
  }

  async playIntroAnimation() {
    const values = { progress: 0 };

    const destPosition = this.camera.position.clone();
    const destQuaternion = this.camera.quaternion.clone();

    const startPosition = new Vector3().addVectors(destPosition, new Vector3(0, 0, -3));
    const startQuaternion = destQuaternion;

    this.disableControls();

    return new Promise(resolve => {
      gsap.to(values, { 
        progress: 1,
        duration: 3,
        onUpdate: (t) => {
          this.camera.position.lerpVectors(startPosition, destPosition, values.progress);
          this.camera.quaternion.slerpQuaternions(startQuaternion, destQuaternion, values.progress);
        },
        onComplete: () => {
          this.enableControls();
          resolve();
        }
      });
    });
  }

  async tweenToHotspot(id) {
    const viewpoint = this.viewpoints[id];
    const values = { progress: 0 };

    const startPosition = this.camera.position.clone();
    const startQuaternion = this.camera.quaternion.clone();

    const destPosition = viewpoint ? viewpoint.position : this._savedPosition;
    const destQuaternion = viewpoint ? viewpoint.quaternion : this._savedQuaternion;

    const w = window.innerWidth;
    const h = window.innerHeight;
    const startViewOffsetX = viewpoint ? 0 : w * 0.25;
    const destViewOffsetX = viewpoint ? w * 0.25 : 0;

    return new Promise(resolve => {
      gsap.to(values, { 
        progress: 1,
        duration: 1,
        ease: Power2.easeInOut,
        onUpdate: (t) => {
          this.camera.position.lerpVectors(startPosition, destPosition, values.progress);
          this.camera.quaternion.slerpQuaternions(startQuaternion, destQuaternion, values.progress);

          if (!window.MOBILE) {
            const offsetX = startViewOffsetX + (destViewOffsetX - startViewOffsetX) * values.progress;
            this.camera.setViewOffset(w, h, offsetX, 0, w, h);
          }
        },
        onComplete: resolve
      });
    });
  }

  update() {
    if (this.orbitControls.enabled) {
      this.orbitControls.update();
    }

    if (this.camera.position.distanceTo(this.lastCameraPosition) > NEAR_ZERO) {
      store.setNeedsRender(true);
    }

    this.lastCameraPosition.copy(this.camera.position);
  }
}